import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { useInstallPWA } from '../contexts/PWAContext';

function InstallPWA() {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const {
    isIOS,
    canInstall,
    handleInstall,
    trackVisit,
    trackDismissal,
    shouldShowBanner,
  } = useInstallPWA();

  const isAuthenticated = keycloak?.authenticated || false;

  // Count the visit once per relevant change (not on canInstall flips, which
  // would inflate the visit count and trip the "2nd visit" threshold early).
  useEffect(() => {
    if (isAuthenticated && isMobile) {
      trackVisit();
    }
  }, [isAuthenticated, isMobile]);

  // Decide whether to show the banner. Depends on canInstall so a late-arriving
  // install prompt (captured after the visit was tracked) still surfaces it.
  useEffect(() => {
    if (isAuthenticated && isMobile && shouldShowBanner()) {
      setShowBanner(true);
    }
  }, [isAuthenticated, isMobile, canInstall]);

  const handleInstallClick = async () => {
    const outcome = await handleInstall();

    if (outcome === 'ios') {
      setShowIOSDialog(true);
    } else if (outcome === 'accepted') {
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    trackDismissal();
  };

  const handleCloseIOSDialog = () => {
    setShowIOSDialog(false);
  };

  // Don't show banner if not authenticated, not mobile, already installed, or shouldn't show
  if (!isAuthenticated || !isMobile || !canInstall || !showBanner) {
    return (
      <>
        {/* iOS Install Instructions Dialog - can be triggered from menu */}
        <Dialog
          open={showIOSDialog}
          onClose={handleCloseIOSDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {t('pwa.iosInstallTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              {t('pwa.iosInstallDescription')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    borderRadius: 1,
                    p: 1,
                    display: 'flex',
                  }}
                >
                  <IosShareIcon sx={{ color: 'white' }} />
                </Box>
                <Typography variant="body2">
                  {t('pwa.iosStep1')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    borderRadius: 1,
                    p: 1,
                    display: 'flex',
                  }}
                >
                  <AddBoxOutlinedIcon sx={{ color: 'white' }} />
                </Box>
                <Typography variant="body2">
                  {t('pwa.iosStep2')}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseIOSDialog}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 0, sm: 16 },
          left: { xs: 0, sm: '50%' },
          right: { xs: 0, sm: 'auto' },
          transform: { xs: 'none', sm: 'translateX(-50%)' },
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
          borderTop: { xs: 1, sm: 0 },
          border: { sm: 1 },
          borderColor: 'divider',
          borderRadius: { xs: 0, sm: 2 },
          px: 1.5,
          py: 1.5,
          boxShadow: 6,
        }}
      >
        <Box
          component="img"
          src="/images/logo-navbar-mobile.svg"
          alt="GlucoScout"
          sx={{ width: 36, height: 36, borderRadius: 1, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="subtitle2" fontWeight="bold" noWrap>
            {t('pwa.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            {t('pwa.installPrompt')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={handleInstallClick}
          sx={{ flexShrink: 0, minWidth: 'auto', px: 2 }}
        >
          {t('pwa.install')}
        </Button>
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label={t('common.close')}
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* iOS Install Instructions Dialog */}
      <Dialog
        open={showIOSDialog}
        onClose={handleCloseIOSDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('pwa.iosInstallTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('pwa.iosInstallDescription')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: 1,
                  p: 1,
                  display: 'flex',
                }}
              >
                <IosShareIcon sx={{ color: 'white' }} />
              </Box>
              <Typography variant="body2">
                {t('pwa.iosStep1')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: 1,
                  p: 1,
                  display: 'flex',
                }}
              >
                <AddBoxOutlinedIcon sx={{ color: 'white' }} />
              </Box>
              <Typography variant="body2">
                {t('pwa.iosStep2')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIOSDialog}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}

export default InstallPWA;
